import { Panel } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { computeWorksheet, money } from "@/dealership/lib/desk";
import { Layers } from "lucide-react";

/** Three-column menu presentation: bundle price + payment difference per period. */
export default function MenuSelling({ ws, settings, onApply }) {
  const bundles = settings?.menu || [];
  const catalog = settings?.products || [];
  if (!bundles.length) return null;

  const base = computeWorksheet({ ...ws, products: [] }, settings);
  const freq = base.frequency_label.toLowerCase();

  const cols = bundles.map((b) => {
    const items = (b.product_ids || []).map((id) => catalog.find((p) => p.id === id)).filter(Boolean)
      .map((p) => ({ ...p, enabled: true }));
    const t = computeWorksheet({ ...ws, products: items }, settings);
    return { ...b, items, totals: t, delta: Math.round((t.payment - base.payment) * 100) / 100 };
  });

  const activeIds = new Set((ws.products || []).filter((p) => p.enabled !== false).map((p) => p.id));
  const isActive = (c) => c.items.length > 0 && c.items.every((i) => activeIds.has(i.id)) && activeIds.size === c.items.length;

  return (
    <Panel label="Menu selling" title="Protection options" icon={Layers} testid="desk-menu-selling">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cols.map((c, i) => {
          const active = isActive(c);
          return (
            <div key={c.id || i} data-testid={`menu-col-${c.id || i}`}
              className={`rounded-2xl border p-4 flex flex-col ${active ? "border-primary bg-primary/[0.06]" : i === 0 ? "border-primary/30 bg-white/[0.02]" : "border-border bg-white/[0.01]"}`}>
              <div className="font-heading text-lg">{c.name}</div>
              <div className="mt-3 space-y-1.5 flex-1">
                {c.items.map((p) => (
                  <div key={p.id} className="text-xs text-muted-foreground flex items-baseline justify-between gap-2">
                    <span className="truncate">{p.name}</span>
                    <span className="tnum shrink-0">{money(p.price)}</span>
                  </div>
                ))}
                {c.items.length === 0 && <div className="text-xs text-muted-foreground">No products configured.</div>}
              </div>
              <div className="mt-4 pt-3 border-t border-border/60">
                <div className="label-mono">{base.frequency_label} payment</div>
                <div className="font-heading text-2xl money tnum mt-0.5" data-testid={`menu-payment-${c.id || i}`}>{money(c.totals.payment)}</div>
                <div className="text-xs mt-1 text-emerald-400" data-testid={`menu-delta-${c.id || i}`}>
                  {c.delta > 0 ? `Only ${money(c.delta)} more ${freq}` : "Same payment"}
                </div>
              </div>
              <Button size="sm" className="mt-4 w-full" variant={active ? "default" : "outline"} onClick={() => onApply({ products: c.items })} data-testid={`menu-apply-${c.id || i}`}>
                {active ? "Selected" : "Apply"}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Base payment with no protection: <span className="money tnum">{money(base.payment)}</span> {freq}.
      </p>
    </Panel>
  );
}
